import { axiosInstance } from '../config/axiosConfig';
import { API_ENDPOINTS } from '../endpoints/endpoints';

export type CampaignStatus = 'ACTIVE' | 'INACTIVE';
export type CampaignChannel =
  | 'INFLUENCER'
  | 'META_ADS'
  | 'GOOGLE_ADS'
  | 'TIKTOK_ADS'
  | 'INSTAGRAM'
  | 'YOUTUBE'
  | 'OTHER';
export type RedirectPlatform = 'IOS' | 'ANDROID';
export type CampaignDevice = 'MOBILE' | 'TABLET' | 'DESKTOP';

export interface Campaign {
  id: string;
  name: string;
  slug: string;
  status: CampaignStatus;
  channel: CampaignChannel;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
  visitCount?: number;
  redirectCount?: number;
  installCount?: number;
}

export interface CreateCampaignRequest {
  name: string;
  slug: string;
  status?: CampaignStatus;
  channel?: CampaignChannel;
  notes?: string;
}

export type UpdateCampaignRequest = Partial<CreateCampaignRequest>;

export interface AnalyticsFilters {
  dateFrom?: string;
  dateTo?: string;
  campaignId?: string;
  device?: CampaignDevice;
  country?: string;
}

export interface AnalyticsOverview {
  totalVisits: number;
  uniqueVisitors: number;
  totalRedirects: number;
  iosClicks: number;
  androidClicks: number;
  totalInstalls: number;
  totalRegistrations: number;
  platformSplit: { ios: number; android: number };
  conversion: {
    visitToRedirect: number;
    redirectToInstall: number;
    installToRegistration: number;
    visitToInstall: number;
  };
}

export interface CampaignAnalyticsRow {
  campaignId: string;
  name: string;
  slug: string;
  status: CampaignStatus;
  channel: CampaignChannel;
  visits: number;
  iosClicks: number;
  androidClicks: number;
  installs: number;
  registrations: number;
  conversion: number;
}

export interface AnalyticsTimeseries {
  dailyVisits: Array<{ date: string; count: number }>;
  downloadsByPlatform: Array<{ date: string; platform: string; count: number }>;
  dailyInstalls: Array<{ date: string; count: number }>;
  dailyRegistrations: Array<{ date: string; count: number }>;
}

export const marketingService = {
  // ===== Public tracking / short-link resolution =====

  async resolveSlug(
    slug: string,
  ): Promise<{ campaignId: string; slug: string; active: boolean }> {
    const res = await axiosInstance.get(API_ENDPOINTS.MARKETING.RESOLVE(slug));
    return res.data;
  },

  /** Fire-and-forget: never let a tracking failure block the redirect. */
  trackVisit(payload: {
    sessionId: string;
    slug?: string;
    referrer?: string;
  }): void {
    axiosInstance
      .post(API_ENDPOINTS.MARKETING.TRACK_VISIT, payload)
      .catch(() => undefined);
  },

  trackRedirect(payload: {
    sessionId: string;
    platform: RedirectPlatform;
    slug?: string;
  }): void {
    axiosInstance
      .post(API_ENDPOINTS.MARKETING.TRACK_REDIRECT, payload)
      .catch(() => undefined);
  },

  // ===== Admin: campaigns =====

  async getCampaigns(): Promise<Campaign[]> {
    const res = await axiosInstance.get(API_ENDPOINTS.MARKETING.CAMPAIGNS);
    return res.data;
  },

  async createCampaign(data: CreateCampaignRequest): Promise<Campaign> {
    const res = await axiosInstance.post(
      API_ENDPOINTS.MARKETING.CAMPAIGNS,
      data,
    );
    return res.data;
  },

  async updateCampaign(
    id: string,
    data: UpdateCampaignRequest,
  ): Promise<Campaign> {
    const res = await axiosInstance.patch(
      API_ENDPOINTS.MARKETING.CAMPAIGN_BY_ID(id),
      data,
    );
    return res.data;
  },

  async deleteCampaign(id: string): Promise<void> {
    await axiosInstance.delete(API_ENDPOINTS.MARKETING.CAMPAIGN_BY_ID(id));
  },

  // ===== Admin: analytics =====

  async getOverview(filters: AnalyticsFilters = {}): Promise<AnalyticsOverview> {
    const res = await axiosInstance.get(
      API_ENDPOINTS.MARKETING.ANALYTICS_OVERVIEW,
      { params: filters },
    );
    return res.data;
  },

  async getPerCampaign(
    filters: AnalyticsFilters = {},
  ): Promise<CampaignAnalyticsRow[]> {
    const res = await axiosInstance.get(
      API_ENDPOINTS.MARKETING.ANALYTICS_CAMPAIGNS,
      { params: filters },
    );
    return res.data;
  },

  async getTimeseries(
    filters: AnalyticsFilters = {},
  ): Promise<AnalyticsTimeseries> {
    const res = await axiosInstance.get(
      API_ENDPOINTS.MARKETING.ANALYTICS_TIMESERIES,
      { params: filters },
    );
    return res.data;
  },
};

export default marketingService;
